import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { Client, ClientDetail, ClientInput } from '@jobos/shared/types/client';
import { useToast } from '@/hooks/use-toast';

interface ClientsResponse {
  clients: Client[];
}

interface ClientDetailResponse {
  client: ClientDetail;
}

// Query keys
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: () => [...clientKeys.lists()] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
};

// Fetch all clients
export function useClients() {
  return useQuery({
    queryKey: clientKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<ClientsResponse>('/clients');
      return response.clients;
    },
  });
}

// Fetch single client by ID
export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: clientKeys.detail(id!),
    queryFn: async () => {
      const response = await apiClient.get<ClientDetailResponse>(`/clients/${id}`);
      return response.client;
    },
    enabled: !!id,
  });
}

// Create client mutation
export function useCreateClient() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: ClientInput) => {
      const response = await apiClient.post<ClientDetailResponse>('/clients', data);
      return response.client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      toast({
        title: 'Success',
        description: 'Client created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create client',
        variant: 'destructive',
      });
    },
  });
}

// Update client mutation
export function useUpdateClient(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<ClientInput>) => {
      const response = await apiClient.put<ClientDetailResponse>(`/clients/${id}`, data);
      return response.client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      toast({
        title: 'Success',
        description: 'Client updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update client',
        variant: 'destructive',
      });
    },
  });
}

// Delete client mutation
export function useDeleteClient() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/clients/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      toast({
        title: 'Success',
        description: 'Client deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete client',
        variant: 'destructive',
      });
    },
  });
}

/**
 * POST /clients/:id/ap-emails — put ONE address on a client's AP list.
 *
 * An append rather than `useUpdateClient({ apEmails })`, which replaces the
 * whole list: the invoice send dialog knows the address it wants on file and
 * not what else is on the list, so PATCHing a list built from its own cached
 * read is how it would drop an address added in another tab.
 *
 * Invalidates the SEND PREVIEWS as well as the client: the address it just
 * added is a contact the open dialog is offering, and a picker that does not
 * show what the user just saved looks broken.
 */
export function useAddClientApEmail(clientId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (email: string) =>
      apiClient.post<{ apEmails: string[] }>(`/clients/${clientId}/ap-emails`, { email }),
    onSuccess: (_data, email) => {
      if (clientId) queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['invoice-sends'] });
      toast({
        title: 'Saved',
        description: `${email} added to this client's billing contacts`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save the address',
        variant: 'destructive',
      });
    },
  });
}
